import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles/SignUpPage.css";
import "../styles/buttons.css";

import { useAuth } from "../utils/authprovider";
import { track } from "../utils/analytics";
import api from "../api";

export default function SignUpPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();
  const { state } = useLocation();
  const incomingType = state?.accountType;
  const { login } = useAuth();

  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title: "Sign Up",
    });
  }, []);

  useEffect(() => {
    document.body.classList.add("sign-up-body");
    return () => document.body.classList.remove("sign-up-body");
  }, []);

  const postAuthRedirect = useCallback(async (userId) => {
    window.gtag?.("set", { user_id: userId });

    if (incomingType) {
      await api.post(
        "/set_user_type/",
        {},
        { params: { user_id: userId, user_type: incomingType }, withCredentials: true }
      );
      await api.get(
        "/email_verification/",
        { params: { user_id: userId }, withCredentials: true }
      );
      navigate(`/user_verification/${userId}`);
      return;
    }
    navigate(`/choose_account/${userId}`);
  }, [incomingType, navigate]);

  const signUpWithEmail = async () => {
    track("sign_up_submit", { method: "email", account_type: incomingType || "unknown" });

    try {
      const { data } = await api.post(
        "/sign_up_user/",
        { username, email, pwd: password },
        { withCredentials: true }
      );

      if (data.status !== "success") {
        setEmailError("An account with this email already exists.");
        track("sign_up_error", {
          method: "email",
          message: "email_exists",
        });
        return;
      }

      login();
      track("sign_up_success", {
        method: "email",
        account_type: incomingType || "unknown",
      });
      window.gtag?.("set", "user_properties", { signup_method: "email" });
      await postAuthRedirect(data.user_id);
    } catch (err) {
      console.error("Sign-up error:", err);
      setEmailError("Sign up failed. Please try again.");
      track("sign_up_error", {
        method: "email",
        message: err.message || "api_error",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let valid = true;
    const missing = [];

    if (!username) {
      setUsernameError("Username is required.");
      valid = false;
      missing.push("username");
    } else {
      setUsernameError("");
    }

    if (!email) {
      setEmailError("Email is required.");
      valid = false;
      missing.push("email");
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError("Invalid email address.");
      valid = false;
      missing.push("email_format");
    } else {
      setEmailError("");
    }

    if (!password) {
      setPasswordError("Password is required.");
      valid = false;
      missing.push("password");
    } else {
      setPasswordError("");
    }

    if (!valid) {
      track("sign_up_validation_error", {
        fields: missing.join(","),
        account_type: incomingType || "unknown",
      });
      return;
    }

    signUpWithEmail();
  };

  return (
    <div className="sign-up-body">
      <div className="sign-in-container">
        <div className="logo">
          <a
            href="/"
            onClick={() =>
              track("navigation_click", { location: "sign_up", nav_item: "Logo", destination: "/" })
            }
          >
            BestDay2Marry
          </a>
        </div>

        <div className="sign-in-form">
          <h1>Sign Up</h1>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={usernameError ? "error" : ""}
              />
              {usernameError && <div className="error-message">{usernameError}</div>}
            </div>

            <div className="form-group">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={emailError ? "error" : ""}
              />
              {emailError && <div className="error-message">{emailError}</div>}
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? "error" : ""}
              />
              {passwordError && <div className="error-message">{passwordError}</div>}
            </div>

            <button type="submit" className="btn btn-primary">
              Create account
            </button>

            <div className="sign-up-container">
              <p id="sign-up-msg">
                Already have an account?&nbsp;
                <a
                  href="/signin"
                  className="sign-up-link"
                  onClick={() =>
                    track("navigation_click", {
                      location: "sign_up",
                      nav_item: "Sign In",
                      destination: "/signin",
                    })
                  }
                >
                  Sign In
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
